import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useParams,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "./styles/sanctuaryGlobal.css"; // Stitch "Academic Sanctuary": Bootstrap primary, body font, toast (applies to public pages too)

import { useAuthContext } from "./context/AuthContext";
import ProtectedRoute from "./components/Auth/ProtectedRoute";

import RequireRole from "./components/Dashboard/guards/RequireRole";
import { ROLES, normalizeRole } from "./components/Dashboard/nav/roles";
import BrandingBootstrap from "./components/BrandingBootstrap";

const lazyPage = (importer) => lazy(importer);

function AppFallback() {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: 14, opacity: 0.8 }}>Loading…</div>
    </div>
  );
}

// shared layout
const NavBar = lazyPage(() => import("./components/Home/Navbar"));
const Footer = lazyPage(() => import("./components/Home/Footer"));

// public
const Login = lazyPage(
  () => import("./components/LOGIN&REGISTRATION/Login/Login"),
);
const Signup = lazyPage(
  () => import("./components/LOGIN&REGISTRATION/Signup/Signup"),
);
const Contact = lazyPage(() => import("./components/Contact/contact"));
const SearchResults = lazyPage(() => import("./components/Home/SearchResults"));
const CasioCalculator = lazyPage(() => import("./components/casio"));

// unified dashboard layout
const DashboardLayout = lazyPage(
  () => import("./components/Dashboard/layout/DashboardLayout"),
);
const DashboardPage = lazyPage(
  () => import("./components/Dashboard/overview/DashboardPage"),
);

// school-scoped admin pages
const AdminUsersPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminUsersPage"),
);
const AdminClassesPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminClassesPage"),
);
const AdminSubjectsPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminSubjectsPage"),
);
const AdminBrandingPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminBrandingPage"),
);
const AdminAIControlsPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminAIControlsPage"),
);
const AdminAnalyticsPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminAnalyticsPage"),
);
const CurriculumMappingPage = lazyPage(
  () => import("./components/Dashboard/pages/CurriculumMappingPage"),
);
const TeacherKnowledgeBasePage = lazyPage(
  () => import("./components/Dashboard/pages/TeacherKnowledgeBasePage"),
);
const AdminKnowledgeBasePage = lazyPage(
  () => import("./components/Dashboard/pages/AdminKnowledgeBasePage"),
);
const QuestionBankPage = lazyPage(
  () => import("./components/Dashboard/pages/QuestionBankPage"),
);
const TeacherAttendancePage = lazyPage(
  () => import("./components/Dashboard/pages/TeacherAttendancePage"),
);
const StudentAttendancePage = lazyPage(
  () => import("./components/Dashboard/pages/StudentAttendancePage"),
);
const AdminAttendancePage = lazyPage(
  () => import("./components/Dashboard/pages/AdminAttendancePage"),
);
const TeacherLiveClassPage = lazyPage(
  () => import("./components/Dashboard/pages/TeacherLiveClassPage"),
);
const StudentLiveClassPage = lazyPage(
  () => import("./components/Dashboard/pages/StudentLiveClassPage"),
);

// portals
const ParentPortal = lazyPage(() => import("./pages/ParentPortal"));
const ParentChildProgressPage = lazyPage(() => import("./pages/ParentChildProgressPage"));
const ParentChildAttendancePage = lazyPage(() => import("./pages/ParentChildAttendancePage"));
const ParentChildLearningPlanPage = lazyPage(() => import("./pages/ParentChildLearningPlanPage"));
const AdminParentLinksPage = lazyPage(
  () => import("./components/Dashboard/pages/AdminParentLinksPage"),
);
const TeacherPortal = lazyPage(() => import("./pages/TeacherPortal"));
const NotificationsPage = lazyPage(() => import("./pages/NotificationsPage"));

// teacher adaptive learning
const ClassProgressPage = lazyPage(
  () => import("./components/Dashboard/pages/teacherLearning/ClassProgressPage"),
);
const StudentProgressForTeacherPage = lazyPage(
  () => import("./components/Dashboard/pages/teacherLearning/StudentProgressForTeacherPage"),
);
const ClassWeakConceptsPage = lazyPage(
  () => import("./components/Dashboard/pages/teacherLearning/adaptive/ClassWeakConceptsPage"),
);
const StudentAdaptiveOverviewPage = lazyPage(
  () => import("./components/Dashboard/pages/teacherLearning/adaptive/StudentAdaptiveOverviewPage"),
);
const ParentChildAdaptiveProgressPage = lazyPage(
  () => import("./pages/ParentChildAdaptiveProgressPage"),
);
const AdminAdaptiveAnalyticsPage = lazyPage(
  () => import("./components/Dashboard/pages/adaptiveLearning/AdminAdaptiveAnalyticsPage"),
);

// superadmin pages
const TenantsPage = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/TenantsPage"),
);
const SuperDashboardPage = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/SuperDashboardPage"),
);
const AICostPage = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/AICostPage"),
);
const AuditLogsPage = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/AuditLogsPage"),
);
const FeatureFlagsPage = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/FeatureFlagsPage"),
);
const SuperAdminAIControlsPage = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/AIControlsPage"),
);
const TenantScopeRedirect = lazyPage(
  () => import("./components/Dashboard/pages/SuperAdmin/TenantScopeRedirect"),
);

// dashboard feature pages
const AITutor = lazyPage(() => import("./components/Dashboard/pages/AITutor"));
const AITutorAvatarPage = lazyPage(() => import("./components/Dashboard/pages/AITutorAvatarPage"));
const Assignments = lazyPage(
  () => import("./components/Dashboard/pages/Assignments"),
);
const PracticeLab = lazyPage(
  () => import("./components/Dashboard/pages/PracticeLab"),
);
const PracticeDetailsPage = lazyPage(
  () => import("./components/Dashboard/pages/PracticeDetailsPage"),
);
const LearningPathPage = lazyPage(
  () => import("./components/Dashboard/pages/adaptiveLearning/LearningPathPage"),
);
const KnowledgeMapPage = lazyPage(
  () => import("./components/Dashboard/pages/adaptiveLearning/KnowledgeMapPage"),
);
const RecommendedRevisionPage = lazyPage(
  () => import("./components/Dashboard/pages/adaptiveLearning/RecommendedRevisionPage"),
);
const TodaysPlanPage = lazyPage(
  () => import("./components/Dashboard/pages/adaptiveLearning/TodaysPlanPage"),
);
const LessonsPage = lazyPage(
  () => import("./components/Dashboard/pages/LessonsPage"),
);
const ModuleDetailsPage = lazyPage(
  () => import("./components/Dashboard/pages/ModuleDetailsPage"),
);
const AnnouncementsPage = lazyPage(
  () => import("./components/Dashboard/pages/AnnouncementsPage"),
);
const GradebookPage = lazyPage(
  () => import("./components/Dashboard/pages/GradebookPage"),
);
const QuizGenerator = lazyPage(
  () => import("./components/Dashboard/pages/QuizGenerator"),
);
const QuizTakingPage = lazyPage(
  () => import("./components/Dashboard/pages/quizzes/QuizTakingPage"),
);
const QuizReviewPage = lazyPage(
  () => import("./components/Dashboard/pages/quizzes/QuizReviewPage"),
);
const BookletStudioList = lazyPage(
  () => import("./components/Dashboard/pages/bookletStudio/BookletStudioList"),
);
const CreateBookletPage = lazyPage(
  () => import("./components/Dashboard/pages/bookletStudio/CreateBookletPage"),
);
const BookletWorkspace = lazyPage(
  () => import("./components/Dashboard/pages/bookletStudio/BookletWorkspace"),
);
const LeaderboardPage = lazyPage(
  () => import("./components/Dashboard/pages/LeaderboardPage"),
);
const NotFoundPage = lazyPage(
  () => import("./components/Dashboard/pages/NotFoundPage"),
);

// "/" and "/login" show Login for guests, redirect authenticated users to dashboard
function LoginOrRedirect() {
  const { isAuthenticated, loading } = useAuthContext();

  if (loading) return <AppFallback />;

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />;
}

// Default dashboard landing by role
function DashboardHome() {
  const { user, loading } = useAuthContext();

  if (loading) return <AppFallback />;

  const role = normalizeRole(user?.role);

  if (role === ROLES.PARENT) {
    return <Navigate to="/dashboard/parent" replace />;
  }

  return <DashboardPage />;
}

function PracticeLabDetailsRedirect() {
  const { practiceId } = useParams();
  return (
    <Navigate
      to={`/dashboard/practice-lab/${encodeURIComponent(practiceId)}/details`}
      replace
    />
  );
}

function QuizStartRedirect() {
  const { quizId } = useParams();
  return (
    <Navigate
      to={`/dashboard/quizzes/${encodeURIComponent(quizId)}/start`}
      replace
    />
  );
}

function QuizReviewRedirect() {
  const { quizId } = useParams();
  return (
    <Navigate
      to={`/dashboard/quizzes/${encodeURIComponent(quizId)}/review`}
      replace
    />
  );
}

const PublicLayout = () => (
  <>
    <NavBar />

    <Outlet />

    <Footer />
  </>
);

const DashboardAppLayout = () => (
  <Suspense fallback={<AppFallback />}>
    <DashboardLayout />
  </Suspense>
);

export default function App() {
  return (
    <BrowserRouter>
      <BrandingBootstrap />
      <ToastContainer
        position="bottom-right"
        theme="colored"
        autoClose={2500}
      />

      <Suspense fallback={<AppFallback />}>
        <Routes>
          {/* ---------- Login: full-screen (Stitch "Academic Sanctuary"), no marketing Navbar/Footer ---------- */}
          <Route path="/" element={<LoginOrRedirect />} />
          <Route path="/login" element={<LoginOrRedirect />} />

          {/* ---------- Public pages with Navbar + Footer ---------- */}
          <Route element={<PublicLayout />}>
            <Route path="/signup" element={<Signup />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/SearchResults" element={<SearchResults />} />
            <Route path="/CasioCalculator" element={<CasioCalculator />} />
          </Route>

          {/* ---------- Protected shell ---------- */}
          <Route
            element={
              <ProtectedRoute>
                <Outlet />
              </ProtectedRoute>
            }
          >
            {/* Legacy redirects — each target below is a real, currently
                registered route. Settings.jsx was removed as dead code
                (commit 2a67e54) and /dashboard/settings was never rebuilt,
                so the old /settings redirect is intentionally NOT kept: it
                pointed at a page that no longer exists, so /settings now
                falls through to the real 404 page directly instead of
                bouncing through a second dead hop. */}
            <Route
              path="/ai-tutor"
              element={<Navigate to="/dashboard/ai-tutor" replace />}
            />
            <Route
              path="/assignments"
              element={<Navigate to="/dashboard/assignments" replace />}
            />
            <Route
              path="/quizzes"
              element={<Navigate to="/dashboard/quizzes" replace />}
            />
            <Route
              path="/quizzes/:quizId/start"
              element={<QuizStartRedirect />}
            />
            <Route
              path="/quizzes/:quizId/review"
              element={<QuizReviewRedirect />}
            />
            <Route
              path="/practice-lab"
              element={<Navigate to="/dashboard/practice-lab" replace />}
            />
            <Route
              path="/practice-lab/:practiceId/details"
              element={<PracticeLabDetailsRedirect />}
            />

            <Route
              path="/admin"
              element={<Navigate to="/dashboard/admin/users" replace />}
            />
            <Route
              path="/super"
              element={
                <Navigate to="/dashboard/superadmin/dashboard" replace />
              }
            />

            {/* Unified dashboard */}
            <Route path="/dashboard" element={<DashboardAppLayout />}>
              <Route index element={<DashboardHome />} />

              {/* ---------- STUDENT ---------- */}
              <Route
                path="assignments"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <Assignments />
                  </RequireRole>
                }
              />
              <Route
                path="quizzes"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <QuizGenerator />
                  </RequireRole>
                }
              />
              <Route
                path="quizzes/:quizId/start"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <QuizTakingPage />
                  </RequireRole>
                }
              />
              <Route
                path="quizzes/:quizId/review"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <QuizReviewPage />
                  </RequireRole>
                }
              />
              <Route
                path="practice-lab"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <PracticeLab />
                  </RequireRole>
                }
              />
              <Route
                path="practice-lab/:practiceId/details"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <PracticeDetailsPage />
                  </RequireRole>
                }
              />
              <Route
                path="learning/path"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <LearningPathPage />
                  </RequireRole>
                }
              />
              <Route
                path="learning/knowledge-map"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <KnowledgeMapPage />
                  </RequireRole>
                }
              />
              <Route
                path="learning/revision"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <RecommendedRevisionPage />
                  </RequireRole>
                }
              />
              <Route
                path="learning/today"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <TodaysPlanPage />
                  </RequireRole>
                }
              />
              <Route
                path="materials"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT, ROLES.TEACHER]}>
                    <LessonsPage />
                  </RequireRole>
                }
              />
              <Route
                path="materials/modules/:moduleId"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT, ROLES.TEACHER]}>
                    <ModuleDetailsPage />
                  </RequireRole>
                }
              />
              <Route
                path="grades"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT, ROLES.TEACHER]}>
                    <GradebookPage />
                  </RequireRole>
                }
              />

              {/* ---------- TEACHER ---------- */}
              <Route
                path="teacher"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <TeacherPortal />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/assignments"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <Assignments />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/quizzes"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <QuizGenerator />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/booklets"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <BookletStudioList />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/booklets/new"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <CreateBookletPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/booklets/:bookletId"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <BookletWorkspace />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/classes"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <ClassProgressPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/question-bank"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <QuestionBankPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/attendance"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <TeacherAttendancePage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/live-class"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <TeacherLiveClassPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/knowledge-base"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <TeacherKnowledgeBasePage />
                  </RequireRole>
                }
              />
              <Route
                path="attendance"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <StudentAttendancePage />
                  </RequireRole>
                }
              />
              <Route
                path="live-class"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT]}>
                    <StudentLiveClassPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/courses/:courseId/progress"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <ClassProgressPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/courses/:courseId/students/:studentId/progress"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <StudentProgressForTeacherPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/adaptive-analytics"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <ClassWeakConceptsPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/courses/:courseId/adaptive-analytics"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <ClassWeakConceptsPage />
                  </RequireRole>
                }
              />
              <Route
                path="teacher/courses/:courseId/students/:studentId/adaptive-overview"
                element={
                  <RequireRole allowedRoles={[ROLES.TEACHER]}>
                    <StudentAdaptiveOverviewPage />
                  </RequireRole>
                }
              />

              {/* ---------- PARENT ---------- */}
              <Route
                path="parent"
                element={
                  <RequireRole allowedRoles={[ROLES.PARENT]}>
                    <ParentPortal />
                  </RequireRole>
                }
              />
              <Route
                path="parent/children/:studentId/progress"
                element={
                  <RequireRole allowedRoles={[ROLES.PARENT]}>
                    <ParentChildProgressPage />
                  </RequireRole>
                }
              />
              <Route
                path="parent/children/:studentId/attendance"
                element={
                  <RequireRole allowedRoles={[ROLES.PARENT]}>
                    <ParentChildAttendancePage />
                  </RequireRole>
                }
              />
              <Route
                path="parent/children/:studentId/learning-plan"
                element={
                  <RequireRole allowedRoles={[ROLES.PARENT]}>
                    <ParentChildLearningPlanPage />
                  </RequireRole>
                }
              />
              <Route
                path="parent/children/:studentId/adaptive-progress"
                element={
                  <RequireRole allowedRoles={[ROLES.PARENT]}>
                    <ParentChildAdaptiveProgressPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/adaptive-analytics"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminAdaptiveAnalyticsPage />
                  </RequireRole>
                }
              />

              {/* ---------- SHARED ---------- */}
              <Route
                path="leaderboard"
                element={
                  <RequireRole
                    allowedRoles={[
                      ROLES.STUDENT,
                      ROLES.TEACHER,
                      ROLES.ADMIN,
                      ROLES.SUPERADMIN,
                      ROLES.PARENT,
                    ]}
                  >
                    <LeaderboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="notifications"
                element={
                  <RequireRole
                    allowedRoles={[
                      ROLES.STUDENT,
                      ROLES.TEACHER,
                      ROLES.ADMIN,
                      ROLES.SUPERADMIN,
                      ROLES.PARENT,
                    ]}
                  >
                    <NotificationsPage />
                  </RequireRole>
                }
              />
              <Route
                path="ai-tutor"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT, ROLES.TEACHER]}>
                    <AITutor />
                  </RequireRole>
                }
              />
              <Route
                path="ai-tutor-avatar"
                element={
                  <RequireRole allowedRoles={[ROLES.STUDENT, ROLES.TEACHER]}>
                    <AITutorAvatarPage />
                  </RequireRole>
                }
              />
              <Route
                path="announcements"
                element={
                  <RequireRole
                    allowedRoles={[
                      ROLES.STUDENT,
                      ROLES.TEACHER,
                      ROLES.ADMIN,
                      ROLES.SUPERADMIN,
                    ]}
                  >
                    <AnnouncementsPage />
                  </RequireRole>
                }
              />

              {/* ---------- ADMIN ---------- */}
              <Route
                path="admin/users"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminUsersPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/workspace"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminClassesPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/subjects"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminSubjectsPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/attendance"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminAttendancePage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/curriculum-mapping"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <CurriculumMappingPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/knowledge-base"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminKnowledgeBasePage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/parent-links"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminParentLinksPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/branding"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminBrandingPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/ai-controls"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminAIControlsPage />
                  </RequireRole>
                }
              />
              <Route
                path="admin/analytics"
                element={
                  <RequireRole allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN]}>
                    <AdminAnalyticsPage />
                  </RequireRole>
                }
              />

              {/* ---------- SUPERADMIN ---------- */}
              <Route
                path="superadmin/dashboard"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <SuperDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/tenants"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <TenantsPage />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/tenants/:tenantId"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <TenantScopeRedirect />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/ai-cost"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <AICostPage />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/feature-flags"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <FeatureFlagsPage />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/audit"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <AuditLogsPage />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/ai-controls"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <SuperAdminAIControlsPage />
                  </RequireRole>
                }
              />
              <Route
                path="superadmin/analytics"
                element={
                  <RequireRole allowedRoles={[ROLES.SUPERADMIN]}>
                    <SuperDashboardPage />
                  </RequireRole>
                }
              />

              <Route path="*" element={<NotFoundPage homeTo="/dashboard" />} />
            </Route>
          </Route>

          {/* ---------- Catch-all ---------- */}
          <Route path="*" element={<NotFoundPage homeTo="/" />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
